import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import {
  createMatterVehicleV3,
  deleteMatterVehicleV1,
  updateMatterVehicleV1,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static";
import {
  type APIError,
  type APIResponse,
  type MatterV3,
  type MatterVehicle,
} from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

const withMatterVehicles = (
  matter: MatterV3,
  vehicles: MatterVehicle[],
): MatterV3 => ({
  ...matter,
  vehicles,
});

export const useCreateMatterVehicle = ({
  showSuccessToast = true,
}: { showSuccessToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterVehicleV3>[0]) =>
      createMatterVehicleV3(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData(
        matterDetailQueryKey(data.matterId),
      );

      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<MatterV3> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: withMatterVehicles(oldData.data, [
              ...oldData.data.vehicles,
              {
                ...data,
                connections: [],
                id: "optimistic",
              },
            ] as MatterVehicle[]),
          };
        },
      );

      return { previousData };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error creating matter vehicle");
    },

    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      if (showSuccessToast) {
        toast.success("Matter vehicle created successfully");
      }
    },
  });
};

export const useUpdateMatterVehicle = ({
  showSuccessToast = true,
}: { showSuccessToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterVehicleV1>[0]) =>
      updateMatterVehicleV1(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData(
        matterDetailQueryKey(data.matterId),
      );

      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<MatterV3> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: withMatterVehicles(
              oldData.data,
              oldData.data.vehicles.map((vehicle) =>
                vehicle.id === data.id ? { ...vehicle, ...data } : vehicle,
              ),
            ),
          };
        },
      );

      return { previousData };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating matter vehicle");
    },

    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      if (showSuccessToast) {
        toast.success("Matter vehicle updated successfully");
      }
    },
  });
};

export const useDeleteMatterVehicle = ({
  showSuccessToast = true,
}: { showSuccessToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterVehicleV1>[0]) =>
      deleteMatterVehicleV1(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData(
        matterDetailQueryKey(data.matterId),
      );

      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<MatterV3> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: withMatterVehicles(
              oldData.data,
              oldData.data.vehicles.filter((vehicle) => vehicle.id !== data.id),
            ),
          };
        },
      );

      return { previousData };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error deleting matter vehicle");
    },

    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      if (showSuccessToast) {
        toast.success("Matter vehicle deleted successfully");
      }
    },
  });
};
